import { t as translateDate } from './translate-date.js';
import './index2.js';

/**
 * **Reservit**
 */
function factory({ window, document, thn }) {
    const engine = {};
    let reservitPriceUpdate;
    /**
       * @memberOf module:bs_149
       * @see {@link prestart}
       *
       * @param {object} ctx - The context object.
       */
    engine.boot = function boot() {
        thn.domReady().then(function () {
            const container = document.querySelector('#ReservitBookingIframe');
            if (container) {
                const reservitPageCheck = thn.debounce(thn.pageCheck, 1000);
                thn.observeDom(container, reservitPageCheck, {
                    childList: false,
                    subtree: true,
                    attributes: true,
                });
                thn.shutdown(function () {
                    reservitPageCheck.cancel();
                    thn.stopObserveDom(container, reservitPageCheck);
                });
            }
            const content = document.querySelector('.global-page');
            if (content) {
                const reservitLanguageUpdate = thn.debounce(thn.languageCheck, 600);
                thn.observeDom(content, reservitLanguageUpdate, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                    characterData: true,
                });
                thn.shutdown(function () {
                    reservitLanguageUpdate.cancel();
                    thn.stopObserveDom(content, reservitLanguageUpdate);
                });
            }
        });
        const loaderContainer = document.querySelector('it-loading');
        if (loaderContainer) {
            const pageUpdate = thn.debounce(() => {
                if (!document.querySelector('#loader-container')) {
                    thn.pageCheck();
                }
            }, 1000);
            thn.observeDom(loaderContainer, pageUpdate, {
                childList: true,
                subtree: true,
                attributes: true,
                characterData: true,
            });
            thn.shutdown(function () {
                pageUpdate.cancel();
                thn.stopObserveDom(loaderContainer, pageUpdate);
            });
        }
    };
    engine.onStateChange = function onStateChange(ctx) {
        setTimeout(function () {
            thn.pageCheck();
        }, 1000);
    };
    engine.priceObserveDom = function priceObserveDom(ctx) {
        const container = !!document.querySelector('#loader-container');
        if (!container && !thn.reservitUpdater) {
            thn.reservitUpdater = thn.debounce(function thnReservitUpdater() {
                // try to refresh the PW data, then hide only if PW data is not set
                if (ctx.widget.refresh() && !ctx.widget.isSet()) {
                    ctx.widget.hide();
                }
                thn.languageCheck().then(function ({ changed }) {
                    if (!changed) {
                        ctx.widget.refresh();
                    }
                });
            }, 800);
            thn.observeDom(container, thn.reservitUpdater, {
                subtree: true,
            });
            thn.pageUnload(function () {
                thn.stopObserveDom(container, thn.reservitUpdater);
                delete thn.reservitUpdater;
            });
        }
        const htmlContainer = document.documentElement;
        if (htmlContainer && !reservitPriceUpdate) {
            const refreshPrice = thn.debounce(() => ctx.widget.update(), 600);
            reservitPriceUpdate = function (mutations) {
                const loaderMutation = [...mutations].map((mutation) => Array.from(mutation?.removedNodes).filter((removeNode) => removeNode?.id === 'loader-container')).filter((results) => !thn.empty(results));
                if (loaderMutation?.length) {
                    refreshPrice();
                }
            };
            thn.observeDom(htmlContainer, reservitPriceUpdate, {
                childList: true,
                subtree: true,
                characterData: true,
                attributes: true,
            });
            thn.pageUnload(function () {
                reservitPriceUpdate = undefined;
                thn.stopObserveDom(htmlContainer, reservitPriceUpdate);
                refreshPrice.cancel();
            });
        }
    };
    /**
       * @memberOf module:bs_149
       * @see {@link generate_search_url}
       *
       * @param {object} ctx - The context object.
       */
    engine.generateSearchUrl = function generateSearchUrl(ctx) {
        const url = new URL(ctx.baseUrl);
        // https://secure.reservit.com/fo/booking/2/12004/availability?nbroom=1&specialMode=default&redirectHOST=hotel.reservit.com&begindate=2024-08-28&partid=0&tmonth=08&tyear=2024&hotelid=12004&numnight=1&m=booking&roomAge1=40&langcode=FR&enddate=2024-08-29&fyear=2024&custid=2&fmonth=08&fday=28&tday=29
        const validUrl = !!url.pathname.match(/booking/);
        if (!validUrl) {
            ctx.newUrl = '';
            return ctx;
        }
        // Get hotel ID
        const dataLayer = window.dataLayer;
        const hotelData = dataLayer.find((item) => item[0] === 'event' && item[1] === 'page_view')?.[2];
        const hotelId = url.searchParams.get('hotelid') || hotelData?.hotelidRIT;
        // Get day, month and year separately
        const dateBegin = ctx.data.dateBegin;
        const dateEnd = ctx.data.dateEnd;
        if (dateBegin && dateEnd) {
            // Date begin data
            const dateBeginParts = dateBegin?.split('/');
            const dateBeginDay = dateBeginParts[2];
            const dateBeginMonth = dateBeginParts[1];
            const dateBeginYear = dateBeginParts[0];
            // Date end data
            const dateEndParts = dateEnd?.split('/');
            const dateEndDay = dateEndParts[2];
            const dateEndMonth = dateEndParts[1];
            const dateEndYear = dateEndParts[0];
            // Add query params
            url.searchParams.set('hotelid', hotelId);
            url.searchParams.set('begindate', '[date_begin]');
            url.searchParams.set('enddate', '[date_end]');
            url.searchParams.set('numnight', '[nights]');
            url.searchParams.set('langcode', '[language]');
            url.searchParams.set('tday', dateBeginDay);
            url.searchParams.set('tmonth', dateBeginMonth);
            url.searchParams.set('tyear', dateBeginYear);
            url.searchParams.set('fday', dateEndDay);
            url.searchParams.set('fmonth', dateEndMonth);
            url.searchParams.set('fyear', dateEndYear);
            ctx.newData.dateBegin = ctx.data.dateBegin.replace(/\//g, '-');
            ctx.newData.dateEnd = ctx.data.dateEnd.replace(/\//g, '-');
            // Export into ctx.newUrl
            ctx.newUrl = url.toString();
        }
        return ctx;
    };
    engine.priceParseData = function priceParseData(ctx) {
        const widgetData = {};
        // Dates
        const formatDate = (date) => date.replace(/[\d.]+|\D+/g, '$& ').replace(/\./g, '');
        const datesInUrl = new URL(window.location).searchParams.has('begindate') && new URL(window.location).searchParams.has('enddate');
        const dateBeginUrl = datesInUrl && (thn.parseUrlParameter('begindate') || '').replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1');
        const dateEndUrl = datesInUrl && (thn.parseUrlParameter('enddate') || '').replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1');
        const dateBeginEl = document.querySelector('div:nth-child(1) > div.form-search-date-info');
        const dateEndEl = document.querySelector('div:nth-child(2) > div.form-search-date-info');
        const dateBeginRaw = dateBeginEl?.textContent && formatDate(dateBeginEl.textContent);
        const dateBegin = dateBeginRaw && translateDate(dateBeginRaw, thn.data.language);
        const dateEndRaw = dateEndEl?.textContent && formatDate(dateEndEl.textContent);
        const dateEnd = dateEndRaw && translateDate(dateEndRaw, thn.data.language);
        // Guests
        const adultsEl = document.querySelector('.room-occupancy > span:nth-child(1)');
        const childEl = document.querySelector('.room-occupancy > span:nth-child(2)');
        const adults = (adultsEl?.textContent && (thn.parseNumber(adultsEl.textContent))) || 2;
        const children = (childEl?.textContent && (thn.parseNumber(childEl.textContent))) || 0; // added fallback when searches do not include any children
        // Price
        const pricesEl = document.querySelectorAll('.offers-container .offer-detail-intro-prices .price-info-price');
        const priceSpecialEl = document.querySelector('.matches');
        const nights = dateBegin && dateEnd && thn.daysBetween(dateBegin, dateEnd);
        const prices = [...pricesEl].map((price) => thn.parseNumber(price.textContent)).filter(Boolean);
        // Currency
        const currencyEl = document.querySelector('it-currency');
        const currencyRaw = pricesEl[0]?.textContent || currencyEl?.textContent;
        const currency = currencyRaw && thn.parseCurrency(currencyRaw);
        if (dateBegin && dateEnd) {
            widgetData.dateBegin = dateBegin;
            widgetData.dateEnd = dateEnd;
        }
        else if (datesInUrl) {
            widgetData.dateBegin = dateBeginUrl;
            widgetData.dateEnd = dateEndUrl;
        }
        widgetData.adults = adults;
        widgetData.children = children;
        widgetData.guests = (adults + children) || 1;
        widgetData.rooms = 1;
        if (prices.length && currency && nights) {
            widgetData.price = Math.min(...prices) / nights;
            widgetData.currency = currency;
            widgetData.set = true;
            widgetData.priceSpecial = priceSpecialEl ? 1 : (document.querySelector('.link-code')?.textContent && 2) || 0;
        }
        widgetData.language = thn.data.language;
        return widgetData;
    };
    engine.bookingParseData = function bookingParseData(ctx) {
        const widgetData = {};
        // Dates from URL but we cannot garantee that they are correct since sometimes the URL is not updated
        const dateBeginYear = thn.parseUrlParameter('fyear');
        const dateBeginMonth = thn.parseUrlParameter('fmonth');
        const dateBeginDay = thn.parseUrlParameter('fday');
        const dateEndYear = thn.parseUrlParameter('tyear');
        const dateEndMonth = thn.parseUrlParameter('tmonth');
        const dateEndDay = thn.parseUrlParameter('tday');
        // Dates from DOM
        const datesFromDom = document.querySelectorAll('.room-sentence b');
        const datesArray = document.querySelector('.final-reserved-recap-coord-text')?.textContent?.match(/\b\d{2}\/\d{2}\/\d{4}\b/g);
        const dateBeginDom = datesFromDom[0]?.textContent || datesArray[0];
        const dateEndDom = datesFromDom[1]?.textContent || datesArray[1];
        // When the language is English, the date format is MM/DD/YYYY
        // When the language is not English, the date format is DD/MM/YYYY
        const dateBeginParam = thn.parseUrlParameter('begindate');
        const dateEndParam = thn.parseUrlParameter('enddate');
        const dateBegin = thn.data.language === 'en' ? (dateBeginDom || dateBeginParam)?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2') : (dateBeginDom || dateBeginParam)?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1');
        const dateEnd = thn.data.language === 'en' ? (dateEndDom || dateEndParam)?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2') : (dateEndDom || dateEndParam)?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1');
        if (dateBegin && dateEnd) {
            widgetData.dateBegin = dateBegin;
            widgetData.dateEnd = dateEnd;
        }
        else if (dateBeginYear && dateBeginMonth && dateBeginDay && dateEndYear && dateEndMonth && dateEndDay) {
            widgetData.dateBegin = `${dateBeginYear}/${dateBeginMonth}/${dateBeginDay}`;
            widgetData.dateEnd = `${dateEndYear}/${dateEndMonth}/${dateEndDay}`;
        }
        const priceEl = (document.querySelector('.price-info-price') || {}).textContent;
        const price = thn.parseNumber(priceEl);
        const currency = thn.parseCurrency(priceEl) || thn.getCurrencyCode(((priceEl || '').match(/[^\d\s,.]+/) || [])[0]);
        if (price && currency) {
            widgetData.price = price;
            widgetData.currency = currency;
        }
        const bookingIdEl = ((document.querySelector('#booking-voucher-info-cpt span') || {}).textContent || '').split(': ');
        widgetData.bookingId = (bookingIdEl || [])[1];
        return widgetData;
    };
    return engine;
}

export { factory };
